import axios from 'axios'
import { defineStore } from 'pinia'
import { BASE_URL } from '@/environment/environment'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { logger } from '@/utils/eventLogger'
import type { IPermissionsStoreState } from '../types/IPermissionsStoreState'
import type { IPermissionsResponse, IModulePermissions } from '../types/IPermissionsResponse'
import type { ICustomAxiosRequestConfig } from '@/types/ICustomAxiosRequestConfig'

export type { IModulePermissions, IPermissionsResponse, IPermissionsStoreState }

/** Ожидающие гард, пока не применён ответ именно на запрос прав */
let readyWaiters: Array<() => void> = []
let permissionsInFlight = false

function releaseReadyWaiters() {
  const waiters = readyWaiters
  readyWaiters = []
  waiters.forEach(resolve => resolve())
}

/**
 * Хранилище прав доступа пользователя к модулям и действиям системы.
 *
 * Основной функционал:
 * - Загрузка полной структуры прав через `GET /api/account/permissions/`
 * - Хранение структурированных флагов действий по модулям (`modules.<модуль>.<действие>`)
 * - Очистка прав при выходе пользователя из системы
 *
 * Использование:
 * - Загружается в `authStore.restoreAuth()` после получения профиля
 * - Сбрасывается в `authStore.clearAuthData()`
 */
export const usePermissionsStore = defineStore('permissionsStore', {
  state: (): IPermissionsStoreState => ({
    modules: {},
    permissions: [],
    isSuperuser: false,
    loading: false,
    loaded: false,
  }),

  getters: {
    /** История изменений в НС, заявках и остальных карточках. */
    canViewChangeHistory(state): boolean {
      if (state.isSuperuser)
        return true
      if (state.permissions.includes('auditlog.viewhistory'))
        return true
      return Boolean(state.modules.auditlog?.viewhistory)
    },
  },

  actions: {
    /**
     * Права ещё неизвестны: гард не должен решать по пустому `modules`.
     * Ставится до старта роутера, сам запрос к API идёт позже.
     */
    beginPending() {
      if (this.loaded || this.loading) return
      this.loading = true
    },

    /**
     * Сессия не дошла до запроса прав. Снимает ожидание гарда.
     */
    cancelPending() {
      if (this.loaded || permissionsInFlight) return
      this.loading = false
      this.loaded = true
      releaseReadyWaiters()
    },

    /**
     * Ждёт завершения текущего запроса прав, если список ещё не заполнен.
     */
    whenReady() {
      if (this.loaded && !this.loading) return Promise.resolve()
      return new Promise<void>(resolve => {
        readyWaiters.push(resolve)
      })
    },

    /**
     * Загружает полную информацию о правах доступа текущего пользователя с бэкенда.
     *
     * @returns {Promise<IPermissionsResponse | undefined>} Ответ с правами или undefined при ошибке
     */
    async fetchPermissions(options?: { speculative?: boolean }) {
      const feedbackStore = useFeedbackStore()
      const speculative = options?.speculative === true
      let speculativeFailed = false
      this.loading = true
      permissionsInFlight = true

      try {
        logger.info('[PermissionsStore] Запрос прав доступа пользователя...')
        const response = await axios.get<IPermissionsResponse>(
          `${BASE_URL}/api/account/permissions/`,
          speculative ? ({ skipAuthLogout: true } as ICustomAxiosRequestConfig) : undefined,
        )
        if (response.status === 200 && response.data) {
          this.modules = response.data.modules || {}
          this.permissions = response.data.permissions || []
          this.isSuperuser = Boolean(response.data.is_superuser)
          logger.info('[PermissionsStore] Права доступа успешно загружены')
          return response.data
        }
      } catch (error) {
        if (speculative) {
          speculativeFailed = true
          throw error
        }
        console.error('❌ Ошибка загрузки прав доступа в permissionsStore:', error)
        logger.error('[PermissionsStore] Ошибка загрузки прав доступа:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить права доступа'),
          time: 7000,
        })
        this.modules = {}
        this.permissions = []
        this.isSuperuser = false
      } finally {
        permissionsInFlight = false
        if (!speculativeFailed) {
          this.loading = false
          this.loaded = true
          releaseReadyWaiters()
        }
      }
    },

    /**
     * Сбрасывает все загруженные права доступа к начальному пустому состоянию.
     * Вызывается при логауте пользователя.
     */
    clearPermissions() {
      this.modules = {}
      this.permissions = []
      this.isSuperuser = false
      this.loading = false
      this.loaded = false
      releaseReadyWaiters()
    },
  },
})
